import type { PackPaymentPage } from '@/services/class-pack-payment';
import type { AppearanceSettings } from '@/state/appearance-settings';
import type { ClassPack, ClassPackGroup } from '@/state/class-pack-groups';
import { formatAmount } from '@/lib/utils';

export const getAllClassPacks = (groups: ClassPackGroup[]): ClassPack[] => {
  return groups.reduce((allPacks, group) => {
    const sortedPacks = [...(group.class_packs || [])].sort(
      (a, b) => a.order - b.order
    );
    return [...allPacks, ...sortedPacks];
  }, [] as ClassPack[]);
};

const isPublicClassPack = (pack: ClassPack): boolean =>
  pack.status === 'ACTIVE' && pack.availability === 'PUBLIC';

export const hasPublicClassPacks = (
  groups: ClassPackGroup[] | undefined
): boolean => !!groups?.some((g) => g.class_packs.some(isPublicClassPack));

export const keepPublicClassPacks = (
  groups: ClassPackGroup[] | undefined
): ClassPackGroup[] | undefined =>
  groups?.map((g) => ({
    ...g,
    class_packs: g.class_packs.filter(isPublicClassPack),
  }));

type PackAsk = Pick<PackPaymentPage, 'price' | 'amount_due' | 'currency'>;

export const packAskAmount = (page: PackAsk): number =>
  page.amount_due ?? page.price;

export const packBalanceLine = (page: PackAsk): string | null => {
  const difference = Math.round((packAskAmount(page) - page.price) * 100) / 100;
  if (difference > 0) {
    return `Includes ${formatAmount(difference, page.currency)} you already owed.`;
  }
  if (difference < 0) {
    return `Your ${formatAmount(-difference, page.currency)} credit was applied.`;
  }
  return null;
};

export const classPackCardFlagsOf = (
  settings: AppearanceSettings['class_pack'] | undefined
) => ({
  showImage: !!(settings?.show_image_in_list ?? settings?.show_image),
  showPrice: !!(settings?.show_price_in_list ?? settings?.show_price),
  showValidity: !!(
    settings?.show_validity_period_in_list ?? settings?.show_validity_period
  ),
  showDescription: settings?.show_description_in_list ?? true,
});
